import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  mediumText,
  smallTextSize,
  extraSmallTextSize,
  largeTextSize,
  borderPrimary,
} from '../../../styling/stylingHelper';
import { canAccessMenuChangeDashboard } from '../../../services/accessManagement/accessManagement';
import { useAuth } from '../../../services/authentication/authContext';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import {
  MenuChangeTemplate,
  TemplateWeek,
  createTemplate as apiCreateTemplate,
  updateTemplate as apiUpdateTemplate,
  deleteTemplate as apiDeleteTemplate,
} from '../../../services/menuChange/menuChangeService';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';

const MenuChangeSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { backendUser, accountSettings } = useAuth();
  const canManage = canAccessMenuChangeDashboard(backendUser, accountSettings);

  const templates = useMenuChangeStore((s) => s.templates);
  const loadTemplates = useMenuChangeStore((s) => s.loadTemplates);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [weeks, setWeeks] = useState<TemplateWeek[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (canManage) loadTemplates();
  }, [canManage, loadTemplates]);

  // Setup uses the same account feature and staff permission as the dashboard.
  if (!canManage) return <Navigate to="/menus/change-dashboard" replace />;

  const loadIntoEditor = (tpl: MenuChangeTemplate | null) => {
    if (tpl) {
      setSelectedId(tpl.id);
      setName(tpl.name);
      setWeeks(tpl.weeks.map((w) => ({ ...w, tasks: w.tasks.map((tk) => ({ ...tk })) })));
    } else {
      setSelectedId(null);
      setName('');
      setWeeks([{ pos: 1, label: '', tasks: [] }]);
    }
    setError('');
  };

  const addWeek = () => setWeeks((prev) => [...prev, { pos: prev.length + 1, label: '', tasks: [] }]);

  const removeWeek = (idx: number) =>
    setWeeks((prev) => prev.filter((_, i) => i !== idx).map((w, i) => ({ ...w, pos: i + 1 })));

  const moveWeek = (idx: number, dir: -1 | 1) => {
    setWeeks((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((w, i) => ({ ...w, pos: i + 1 }));
    });
  };

  const setWeekLabel = (idx: number, label: string) =>
    setWeeks((prev) => prev.map((w, i) => (i === idx ? { ...w, label } : w)));

  const addTaskToWeek = (idx: number) =>
    setWeeks((prev) => prev.map((w, i) => (i === idx ? { ...w, tasks: [...w.tasks, { label: '' }] } : w)));

  const setTaskLabel = (weekIdx: number, taskIdx: number, label: string) =>
    setWeeks((prev) =>
      prev.map((w, i) =>
        i === weekIdx ? { ...w, tasks: w.tasks.map((tk, ti) => (ti === taskIdx ? { label } : tk)) } : w
      )
    );

  const removeTask = (weekIdx: number, taskIdx: number) =>
    setWeeks((prev) =>
      prev.map((w, i) => (i === weekIdx ? { ...w, tasks: w.tasks.filter((_, ti) => ti !== taskIdx) } : w))
    );

  const canSave = name.trim() && weeks.length > 0 && weeks.every((w) => w.label.trim());

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError('');
    // Drop empty task labels before persisting.
    const cleanedWeeks: TemplateWeek[] = weeks.map((w, i) => ({
      pos: i + 1,
      label: w.label.trim(),
      tasks: w.tasks.filter((tk) => tk.label.trim()).map((tk) => ({ label: tk.label.trim() })),
    }));
    try {
      if (selectedId) {
        await apiUpdateTemplate(selectedId, { name: name.trim(), weeks: cleanedWeeks });
      } else {
        await apiCreateTemplate({ name: name.trim(), weeks: cleanedWeeks });
      }
      await loadTemplates(true);
      loadIntoEditor(null);
    } catch (err) {
      console.error('Failed to save template:', err);
      setError(t('menuChange.templateSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiDeleteTemplate(id);
      await loadTemplates(true);
      if (selectedId === id) loadIntoEditor(null);
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const isEditing = weeks.length > 0;

  return (
    <div>
      <MobilePageHeader title={t('menuChange.setupTitle')} />
      {/* Back header (OrderHistory parent→child pattern). */}
      <div className="flex items-center mb-6">
        <ArrowLeft className="w-6 h-6 mt-1 mr-2 cursor-pointer" onClick={() => navigate('/menus/change-dashboard')} />
        <h1 className={cn('text-2xl font-semibold', primaryText, 'hidden lg:block')}>{t('menuChange.setupTitle')}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template list */}
        <div className={cn('lg:col-span-1 bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark p-4')}>
          <div className="flex items-center justify-between mb-4">
            <p className={cn(largeTextSize, semiBoldText, primaryText)}>{t('menuChange.templates')}</p>
            <button onClick={() => loadIntoEditor(null)} className={cn('flex items-center gap-1', extraSmallTextSize, mediumText, 'text-[#0E8965] dark:text-[#0E8965]')}>
              <Plus className="w-3.5 h-3.5" />
              {t('menuChange.newTemplate')}
            </button>
          </div>
          {templates.length === 0 ? (
            <p className={cn(smallTextSize, secondaryText)}>{t('menuChange.noTemplates')}</p>
          ) : (
            <div className="space-y-2">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className={cn(
                    'flex items-center justify-between gap-2 px-3 py-2 rounded-lg border cursor-pointer',
                    selectedId === tpl.id ? 'border-[#0E8965] dark:border-[#0E8965] bg-emerald-50 dark:bg-emerald-900/20' : borderPrimary
                  )}
                  onClick={() => loadIntoEditor(tpl)}
                >
                  <span className={cn(smallTextSize, mediumText, primaryText, 'truncate')}>{tpl.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(tpl.id);
                    }}
                    className="text-zinc-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0"
                    aria-label={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor */}
        <div className={cn('lg:col-span-2 bg-white dark:bg-background-dark rounded-xl border border-transparent dark:border-borderPrimary-dark p-4 sm:p-6')}>
          {!isEditing ? (
            <p className={cn(smallTextSize, secondaryText)}>{t('menuChange.selectOrCreateTemplate')}</p>
          ) : (
            <div className="space-y-5">
              <div>
                <label className={cn('block mb-1', smallTextSize, mediumText, secondaryText)}>{t('menuChange.templateName')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('menuChange.templateNamePlaceholder')}
                  className={cn('w-full px-3 py-2 rounded-xl border bg-white dark:bg-zinc-800 outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500', borderPrimary, primaryText)}
                />
              </div>

              <div className="space-y-3">
                {weeks.map((week, wIdx) => (
                  <div key={wIdx} className={cn('rounded-xl border p-3', borderPrimary)}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={cn(extraSmallTextSize, semiBoldText, secondaryText, 'w-10')}>W{week.pos}</span>
                      <input
                        type="text"
                        value={week.label}
                        onChange={(e) => setWeekLabel(wIdx, e.target.value)}
                        placeholder={t('menuChange.weekLabelPlaceholder')}
                        className={cn('flex-1 px-2 py-1.5 rounded-lg border bg-transparent dark:bg-transparent outline-none', smallTextSize, borderPrimary, primaryText)}
                      />
                      <button onClick={() => moveWeek(wIdx, -1)} disabled={wIdx === 0} className="text-zinc-400 dark:text-zinc-500 disabled:opacity-30" aria-label={t('menuChange.moveUp')}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveWeek(wIdx, 1)} disabled={wIdx === weeks.length - 1} className="text-zinc-400 dark:text-zinc-500 disabled:opacity-30" aria-label={t('menuChange.moveDown')}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeWeek(wIdx)} className="text-zinc-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400" aria-label={t('menuChange.removeWeek')}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="pl-12 space-y-2">
                      {week.tasks.map((task, tIdx) => (
                        <div key={tIdx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={task.label}
                            onChange={(e) => setTaskLabel(wIdx, tIdx, e.target.value)}
                            placeholder={t('menuChange.taskLabelPlaceholder')}
                            className={cn('flex-1 px-2 py-1.5 rounded-lg border bg-transparent dark:bg-transparent outline-none', extraSmallTextSize, borderPrimary, primaryText)}
                          />
                          <button onClick={() => removeTask(wIdx, tIdx)} className="text-zinc-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400" aria-label={t('menuChange.removeTask')}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => addTaskToWeek(wIdx)} className={cn('flex items-center gap-1', extraSmallTextSize, mediumText, 'text-[#0E8965] dark:text-[#0E8965]')}>
                        <Plus className="w-3.5 h-3.5" />
                        {t('menuChange.addTask')}
                      </button>
                    </div>
                  </div>
                ))}

                <button onClick={addWeek} className={cn('flex items-center gap-1', smallTextSize, mediumText, 'text-[#0E8965] dark:text-[#0E8965]')}>
                  <Plus className="w-4 h-4" />
                  {t('menuChange.addWeek')}
                </button>
              </div>

              {error && <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>}

              <div className="flex justify-end gap-3">
                <button onClick={() => loadIntoEditor(null)} className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 rounded-xl hover:bg-zinc-200 dark:hover:bg-zinc-700">
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave || isSaving}
                  className="px-4 py-2 text-sm font-medium text-white dark:text-white bg-green-600 dark:bg-green-600 rounded-xl hover:bg-green-700 dark:hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuChangeSetupPage;
